import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import StarRating from '../components/StarRating';
import { getImageUrl, PLACEHOLDER_IMAGE } from '../utils/imageUrl';
import { useCart } from '../context/CartContext';
import { getProduto, getProdutos } from '../services/api';
import './ProdutoDetalhe.css';

const ProdutoDetalhe = () => {
  const { id } = useParams();
  const { addItem } = useCart();
  const [produto, setProduto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fotoPrincipal, setFotoPrincipal] = useState(null);
  const [corSelecionada, setCorSelecionada] = useState(null);
  const [produtosMesmaCategoria, setProdutosMesmaCategoria] = useState([]);

  useEffect(() => {
    loadProduto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadProduto = async () => {
    try {
      const response = await getProduto(id);
      const produtoData = response.data;
      setProduto(produtoData);
      setCorSelecionada(null);
      loadProdutosMesmaCategoria(produtoData);

      // Se for coloração e tiver cores, foto principal pode ser da primeira cor
      const cores = produtoData.cores || [];
      if (cores.length > 0 && cores[0].foto) {
        setFotoPrincipal(cores[0].foto);
      } else {
        const fotos = produtoData.fotos || (produtoData.foto ? [produtoData.foto] : []);
        if (fotos.length > 0) setFotoPrincipal(fotos[0]);
      }

      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar produto:', error);
      setLoading(false);
    }
  };

  const loadProdutosMesmaCategoria = async (produtoAtual) => {
    if (!produtoAtual || !produtoAtual.categoria_id) return;
    try {
      const params = { categoria_id: produtoAtual.categoria_id, limit: 20 };
      const response = await getProdutos(params);
      const lista = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      const outros = lista.filter(p => p.id !== produtoAtual.id);
      setProdutosMesmaCategoria(outros.slice(0, 16));
    } catch (error) {
      console.error('Erro ao carregar produtos da categoria:', error);
      setProdutosMesmaCategoria([]);
    }
  };

  const handleAddToCart = () => {
    const cores = produto.cores || [];
    if (cores.length > 0 && !corSelecionada) {
      toast.info('Selecione uma opcao antes de adicionar ao carrinho.');
      return;
    }
    addItem(produto, 1, { corSelecionada: corSelecionada || undefined });
    toast.success('Produto adicionado ao carrinho!');
  };

  if (loading) {
    return <div className="loading">Carregando produto...</div>;
  }

  if (!produto) {
    return <div className="no-product">Produto não encontrado</div>;
  }

  const notaMedia = parseFloat(produto.nota_media) || 0;
  const estrelas = Math.round(notaMedia);

  return (
    <div className="produto-detalhe">
      <Helmet>
        <title>{produto.nome} | Loucas por Beleza</title>
        <meta name="description" content={produto.descricao ? produto.descricao.substring(0, 160) : `Compre ${produto.nome} na Loucas por Beleza`} />
      </Helmet>
      <div className="container">
        <div className="produto-content">
          <div className="produto-image-section">
            {(() => {
              const cores = produto.cores || [];
              const fotos = produto.fotos || (produto.foto ? [produto.foto] : []);
              const isVariacoesGallery = cores.length > 0;
              const primeiraFotoVariacao = cores.find((c) => !!c.foto)?.foto || null;
              const fotoAtual = fotoPrincipal || (isVariacoesGallery ? primeiraFotoVariacao : null) || fotos[0] || produto.foto;
              
              if (isVariacoesGallery || fotos.length > 0 || produto.foto) {
                return (
                  <div className="produto-galeria-container">
                    {isVariacoesGallery ? (
                      <div className="produto-thumbnails">
                        {cores.map((cor, index) => (
                          <div
                            key={index}
                            className={`thumbnail-item ${corSelecionada?.descricao_cor === cor.descricao_cor ? 'active' : ''} ${fotoAtual === cor.foto ? 'active' : ''}`}
                            onClick={() => {
                              setCorSelecionada({ foto: cor.foto, descricao_cor: cor.descricao_cor });
                              if (cor.foto) setFotoPrincipal(cor.foto);
                            }}
                            onMouseEnter={() => { if (cor.foto) setFotoPrincipal(cor.foto); }}
                            title={cor.descricao_cor}
                          >
                            {cor.foto ? (
                              <img src={getImageUrl(cor.foto)} alt={cor.descricao_cor} onError={(e) => { e.target.style.display = 'none'; }} />
                            ) : (
                              <span>{cor.descricao_cor}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : fotos.length > 1 && (
                      <div className="produto-thumbnails">
                        {fotos.map((foto, index) => (
                          <div
                            key={index}
                            className={`thumbnail-item ${foto === fotoAtual ? 'active' : ''}`}
                            onMouseEnter={() => setFotoPrincipal(foto)}
                            onClick={() => setFotoPrincipal(foto)}
                          >
                            <img src={getImageUrl(foto)} alt={`${produto.nome} - Foto ${index + 1}`} onError={(e) => { e.target.style.display = 'none'; }} />
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="produto-foto-principal">
                      <img 
                        src={getImageUrl(fotoAtual)} 
                        alt={produto.nome}
                        onError={(e) => {
                          e.target.src = getImageUrl(PLACEHOLDER_IMAGE);
                        }}
                      />
                    </div>
                  </div>
                );
              }
              return <div className="no-image-large">Sem Imagem</div>;
            })()}
          </div>

          <div className="produto-info-card">
            <div className="produto-info-section">
              <h1>{produto.nome}</h1>
              
              <div className="produto-rating-section">
                <StarRating rating={estrelas} />
                <span className="rating-text">
                  {notaMedia.toFixed(1)} ({produto.total_avaliacoes || 0} avaliações)
                </span>
              </div>

              <div className="produto-price-section">
                {produto.preco_anterior && (
                  <span className="old-price">R$ {parseFloat(produto.preco_anterior).toFixed(2)}</span>
                )}
                <span className="current-price">R$ {parseFloat(produto.preco).toFixed(2)}</span>
              </div>

              {produto.estoque_quantidade !== undefined && (
                <div className="stock-info">
                  {produto.estoque_quantidade > 0 ? (
                    <span className="in-stock">✓ Em estoque ({produto.estoque_quantidade} unidades)</span>
                  ) : (
                    <span className="out-of-stock">✗ Fora de estoque</span>
                  )}
                </div>
              )}

              {(() => {
                const cores = produto.cores || [];
                if (cores.length === 0) return null;
                return (
                  <div className="produto-cores-section">
                    <h3>Escolha uma opcao</h3>
                    <p className="produto-cores-hint">Selecione a variacao desejada (cor, tamanho ou outra opcao) e depois adicione ao carrinho.</p>
                    <div className="produto-cores-grid">
                      {cores.map((cor, index) => (
                        <button
                          key={index}
                          type="button"
                          className={`produto-cor-item ${corSelecionada?.descricao_cor === cor.descricao_cor ? 'selected' : ''}`}
                          onClick={() => {
                            setCorSelecionada({ foto: cor.foto, descricao_cor: cor.descricao_cor });
                            if (cor.foto) setFotoPrincipal(cor.foto);
                          }}
                          title={cor.descricao_cor}
                        >
                          <div className="produto-cor-foto">
                            {cor.foto ? <img src={getImageUrl(cor.foto)} alt={cor.descricao_cor} /> : <span>{cor.descricao_cor}</span>}
                          </div>
                          <span className="produto-cor-nome">{cor.descricao_cor}</span>
                        </button>
                      ))}
                    </div>
                    {corSelecionada && (
                      <p className="produto-cor-selecionada">Opcao selecionada: <strong>{corSelecionada.descricao_cor}</strong></p>
                    )}
                  </div>
                );
              })()}

              <div className="produto-description">
                <h3>Descrição</h3>
                <p>{produto.descricao || 'Sem descrição disponível.'}</p>
              </div>

              <button type="button" className="btn-add-cart" onClick={handleAddToCart}>
                Adicionar ao Carrinho
              </button>
            </div>
          </div>
        </div>

        {/* Produtos da mesma categoria/subcategoria - carrossel */}
        {produtosMesmaCategoria.length > 0 && (
          <section className="produtos-mesma-categoria-section">
            <h2 className="produtos-mesma-categoria-title">
              Mais em {produto.categoria_nome || 'esta categoria'}
            </h2>
            <div className="produtos-mesma-categoria-carrossel">
              {produtosMesmaCategoria.map((p) => {
                const foto = (p.fotos && p.fotos[0]) || p.foto;
                const precoAtual = parseFloat(p.preco) || 0;
                const precoAntigo = p.preco_anterior ? parseFloat(p.preco_anterior) : 0;
                const temDesconto = precoAntigo > precoAtual && precoAntigo > 0;
                const percentualDesconto = temDesconto ? Math.round((1 - precoAtual / precoAntigo) * 100) : 0;
                return (
                  <Link key={p.id} to={`/produto/${p.id}`} className="produto-mesma-categoria-card">
                    <div className="produto-mesma-categoria-card-image">
                      {foto ? (
                        <img src={getImageUrl(foto)} alt={p.nome} loading="lazy" />
                      ) : (
                        <div className="no-image">Sem imagem</div>
                      )}
                      {temDesconto && (
                        <span className="produto-mesma-categoria-card-desconto">-{percentualDesconto}%</span>
                      )}
                    </div>
                    <div className="produto-mesma-categoria-card-info">
                      <span className="produto-mesma-categoria-card-nome">{p.nome}</span>
                      <span className="produto-mesma-categoria-card-preco">R$ {precoAtual.toFixed(2)}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ProdutoDetalhe;

